import type { MenuConfig } from '@/services/ConfigService';

/**
 * Default navigation structure for the application.
 * This is the single source of truth for the nav menu layout.
 *
 * Labels can be overridden via the module configuration system
 * (system_configuration → module.navigation). The DB no longer
 * stores the full navigation structure (work item #28338).
 */
export const DEFAULT_NAVIGATION: MenuConfig = {
  groups: {
    property_book: {
      display_name: 'Property Book',
      icon: 'Book',
      category_leaves: [
        {
          key: 'reserved-stock',
          label: 'Saved Issue Carts',
          icon: 'ShoppingCartCheckout',
          route: '/property-book/reserved-stock',
          entity_type: '',
          viewerHidden: true,
          comptrollerHidden: true,
        },
        {
          key: 'asset-management',
          label: 'Assets',
          entity_type: 'asset',
          icon: 'Inventory2',
        },
        {
          key: 'purchase-requisitions',
          label: 'Purchase Requisitions',
          icon: 'ShoppingCart',
          route: '/purchase-requisitions',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'loss-reporting',
          label: 'Loss Reporting',
          icon: 'Report',
          route: '/asset-loss-management',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'iar',
          label: 'IAR',
          icon: 'Inventory',
          route: '/iar',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'maintenance-request',
          label: 'Maintenance Request',
          icon: 'Build',
          route: '/maintenance-request',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'shipments',
          label: 'Transfers',
          icon: 'LocalShipping',
          route: '/shipping',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'bulk-inventory-bin',
          label: 'Bulk Inventory Bin',
          icon: 'Inventory',
          entity_type: 'bulk_inventory_bin',
          route: '/bulk-inventory-bin',
          comptrollerHidden: true,
        },
        {
          key: 'bulk-dispose',
          label: 'Bulk Dispose',
          icon: 'Warning',
          route: '/bulk-dispose',
          entity_type: '',
          comptrollerHidden: true,
        },
        {
          key: 'barcode-labels',
          label: 'Barcode Labels',
          icon: 'QrCode2',
          route: '/barcode-labels',
          entity_type: '',
          comptrollerHidden: true,
        },
      ],
    },
    front_window: {
      display_name: 'Front Window',
      icon: 'Storefront',
      entity_order: ['customer_service'],
    },
    admin: {
      display_name: 'Management',
      icon: 'AdminPanelSettings',
      category_leaves: [
        {
          key: 'catalog-management',
          label: 'Catalog Management',
          entity_type: 'catalog',
          icon: 'MenuBook',
          comptrollerHidden: true,
        },
        {
          key: 'people-management',
          label: 'People Management',
          entity_type: 'person',
          icon: 'Person',
          comptrollerHidden: true,
        },
        {
          key: 'location-management',
          label: 'Location Management',
          entity_type: 'location',
          icon: 'LocationOn',
          comptrollerHidden: true,
        },
        {
          key: 'ie-kit-management',
          label: 'IE Kit Management',
          entity_type: 'ie_kit',
          icon: 'Assignment',
        },
        {
          key: 'user-management',
          label: 'User Management',
          icon: 'ManageAccounts',
          route: '/users',
          entity_type: '',
          adminOnly: true,
        },
        {
          key: 'email-management',
          label: 'Email Management',
          icon: 'MailOutline',
          route: '/email',
          entity_type: '',
          adminOnly: true,
        },
        {
          key: 'configuration-management',
          label: 'Configuration Management',
          icon: 'Settings',
          route: '/configuration',
          entity_type: '',
          adminOnly: true,
        },
        {
          key: 'system-health',
          label: 'System Health',
          icon: 'MonitorHeart',
          route: '/system-health',
          entity_type: '',
          adminOnly: true,
        },
        {
          key: 'system-logs',
          label: 'System Logs',
          icon: 'Terminal',
          route: '/system-logs',
          entity_type: '',
          adminOnly: true,
        },
      ],
    },
  },
  group_order: ['property_book', 'front_window', 'admin'],
  ungrouped_entity_order: [],
  placeholder_routes: [],
};

/**
 * Apply label overrides to the default navigation config.
 * overrides is a Record<navItemKey, newLabel> stored in module.navigation config.
 */
export function applyNavLabelOverrides(
  base: MenuConfig,
  overrides: Record<string, string>,
): MenuConfig {
  if (!overrides || Object.keys(overrides).length === 0) return base;

  const result: MenuConfig = JSON.parse(JSON.stringify(base));

  // Override group display_names
  for (const [gKey, gDef] of Object.entries(result.groups)) {
    const overrideKey = `group:${gKey}`;
    if (overrides[overrideKey]) {
      gDef.display_name = overrides[overrideKey];
    }

    // Override category_leaves labels
    if (gDef.category_leaves) {
      for (const leaf of gDef.category_leaves) {
        const leafOverride = overrides[leaf.key];
        if (leafOverride) {
          leaf.label = leafOverride;
        }
      }
    }
  }

  // Override placeholder_routes labels
  if (result.placeholder_routes) {
    for (const route of result.placeholder_routes) {
      const routeOverride = overrides[route.key];
      if (routeOverride) {
        route.label = routeOverride;
      }
    }
  }

  return result;
}
